import { MD5 } from 'crypto-js';
import { useMemo, useState } from 'react';
import React from 'react';
import { toast } from 'sonner';

import { Loader } from '@/components/Loader';
import { useMy } from '@/hooks';
import { useNextRun } from '@/hooks/useNextRun';
import { useScanLevel } from '@/hooks/useScanLevel';
import { useScanProfiles } from '@/hooks/useScanProfiles';
import { useScanSchedule } from '@/hooks/useScanSchedule';
import { JobsScanStatus } from '@/sections/jobs/components/actions';
import {
  RateLimitingForm,
  ScanLevelSetupFormComponent,
  ScanProfilesForm,
  ScanSchedule,
  ScanStatusTag,
  SettingActionCard,
  SettingEditableCard,
  SourceIPForm,
} from '@/sections/settings/components';
import { RATE_LIMIT_SYSTEM_DEFAULTS } from '@/sections/settings/constants';
import { useAuth } from '@/state/auth';
import { AssetStatusLabel, FlagType, STATIC_IP_ADDRESS } from '@/types';
import { cn } from '@/utils/classname';

interface ScanSettingsCardsProps {
  /**
   * Email for impersonation - if provided, will fetch data for this user
   * If not provided, uses the current authenticated user
   */
  email?: string;

  /**
   * Whether to show edit buttons (default: true)
   */
  editable?: boolean;
}

/**
 * Compact 5-card layout for scan settings configuration
 * Displays: Scan Header, Scan Level, Source IP, Scan Schedule, and Rate Limiting
 *
 * Cards:
 * 1. Scan Header - Read-only MD5 identifier for scan whitelisting
 * 2. Scan Level - Scan intensity (light/medium/heavy) with status and next run
 * 3. Source IP - Static vs dynamic IP configuration
 * 4. Scan Schedule - Days and time window settings
 * 5. Rate Limiting - Simultaneous hosts and request rate limits
 *
 * Caching strategy:
 * - staleTime: 30s - Settings change infrequently, 30s staleness acceptable
 * - gcTime: 5min - Keep in cache during typical navigation patterns
 * - Impersonation-safe: Query keys include email for isolation
 *
 * Used in:
 * - Customer Management Drawer Settings Tab
 * - User Settings Page Scan Settings Tab
 */
export const ScanSettingsCards: React.FC<ScanSettingsCardsProps> = ({
  email,
  editable = true,
}) => {
  // Auth for scan header computation
  const { me, friend, isPraetorianUser } = useAuth();

  // MD5 hash computation is expensive (>50ms), keep in useMemo
  // This is the unique scan identifier for whitelisting purposes
  const header = useMemo(
    () => MD5(email || friend || me).toString(),
    [email, friend, me]
  );

  // Copy state for visual feedback
  const [headerCopied, setHeaderCopied] = useState(false);

  const handleCopyScanHeader = async () => {
    try {
      await navigator.clipboard.writeText(`Chariot: ${header}`);
      toast.success('Scan header copied to clipboard');
      setHeaderCopied(true);
      setTimeout(() => setHeaderCopied(false), 1500);
    } catch (error) {
      console.error('Clipboard write failed:', error);
      toast.error('Failed to copy scan header');
    }
  };

  // Scan settings hooks with email support for impersonation
  const { nextRunText } = useNextRun({ email });
  const { refetch: refreshJobsData } = useMy(
    {
      resource: 'job',
      query: '',
    },
    {
      email,
      enabled: false,
    }
  );
  const {
    currentScanLevel,
    isFrozen,
    status: scanLevelStatus,
  } = useScanLevel({
    email,
  });
  const { currentScanSchedule, isLoadingScanSchedule } = useScanSchedule({
    email,
  });

  // Rate limiting settings with standard caching configuration
  // staleTime: 30s prevents excessive refetching for infrequently-changing settings data
  // gcTime: 5min keeps data cached during typical navigation patterns
  const { data: customerSettings, status: rateLimitingStatus } = useMy(
    {
      resource: 'setting',
    },
    {
      email,
      staleTime: 30000, // 30 seconds - reasonable for settings data
      gcTime: 300000, // 5 minutes - keep during navigation
    }
  );

  // Feature flags for Source IP configuration
  // Same caching strategy as other settings
  const { data: flags, status: flagsStatus } = useMy(
    {
      resource: 'flag',
    },
    {
      email,
      staleTime: 30000,
      gcTime: 300000,
    }
  );

  const flagsData = React.useMemo(() => {
    return (flags || []).map(flag => flag.name);
  }, [flags]);

  const rateLimitSetting = React.useMemo(() => {
    const setting = customerSettings?.find(
      setting => setting.name === 'rate-limit'
    )?.value as
      | {
          simultaneousHosts?: number;
          capabilityRateLimit?: number;
        }
      | undefined;
    return setting;
  }, [customerSettings]);

  const { profiles: scanProfiles } = useScanProfiles(email);

  const isLoadingFlags = flagsStatus === 'pending';

  return (
    <div className="flex flex-col gap-2">
      {/* Scan Status Card - Display Only with JobsScanStatus controls */}
      <SettingActionCard
        data-testid="scan-status-card"
        action={
          editable ? (
            <JobsScanStatus
              nextRunText={nextRunText}
              refetch={refreshJobsData}
              email={email}
            />
          ) : undefined
        }
      >
        <div className="flex items-center gap-2">
          <h4 className="font-bold">Scan Status:</h4>
          <ScanStatusTag isFrozen={isFrozen} className="py-0.5" />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-chariot-text-secondary text-xs">
            {isFrozen ? 'Scans are paused' : `Next run: ${nextRunText}`}
          </span>
        </div>
      </SettingActionCard>

      {/* Scan Header Card - Display Only with copy button */}
      <SettingActionCard
        data-testid="scan-header-card"
        action={
          <button
            onClick={handleCopyScanHeader}
            className={cn(
              'rounded-md px-3 py-1.5 font-mono text-sm font-semibold transition-colors',
              headerCopied
                ? 'bg-green-500/10 text-green-600 ring-1 ring-green-500/20'
                : 'bg-chariot-background-secondary text-chariot-text-primary hover:bg-chariot-background-tertiary cursor-pointer'
            )}
            aria-label="Copy scan header to clipboard"
            type="button"
            data-testid="scan-header-copy-button"
          >
            Chariot: {header}
          </button>
        }
      >
        <div className="flex items-center gap-2">
          <h4 className="font-bold">Scan Header</h4>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-chariot-text-secondary text-xs">
            Unique identifier included in all scans
          </span>
        </div>
      </SettingActionCard>

      {/* Scan Level Card */}
      <SettingEditableCard
        data-testid="scan-level-card"
        editConfig={{
          modalTitle: 'Scan Levels',
          formComponent: ScanLevelSetupFormComponent,
          formProps: { customerEmail: email },
          strategy: 'url-state',
          urlParam: 'scan-level',
          modalSize: '4xl',
          footer: {
            text: 'Apply',
            form: 'scan-level-setup-form',
          },
        }}
        editable={editable}
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <h4 className="font-bold">Scan Level:</h4>
            <Loader
              className="text-chariot-text-secondary w-12"
              isLoading={scanLevelStatus === 'pending'}
            >
              {AssetStatusLabel[currentScanLevel]}
            </Loader>
          </div>
          <span className="text-chariot-text-secondary text-xs">
            Controls scan intensity and coverage depth
          </span>
        </div>
      </SettingEditableCard>

      {/* Source IP Card */}
      <SettingEditableCard
        data-testid="source-ip-card"
        editConfig={{
          modalTitle: 'Source IP Configuration',
          formComponent: SourceIPForm,
          formProps: { customerEmail: email },
          modalSize: 'xl',
          modalClassName: 'px-6 py-5',
        }}
        editable={editable && isPraetorianUser}
      >
        <div className="flex items-center gap-2">
          <h4 className="font-bold">Source IP Addresses</h4>
          {!flagsData.includes(FlagType.STATIC) && (
            <span className="text-chariot-text-tertiary text-xs">
              (Default)
            </span>
          )}
        </div>

        <div className="flex items-start gap-2">
          <h4 className="text-chariot-text-secondary min-w-[60px] font-bold">
            Mode:
          </h4>
          <Loader
            className="text-chariot-text-primary"
            isLoading={isLoadingFlags}
          >
            <span className="flex items-center gap-1.5">
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  flagsData.includes(FlagType.STATIC)
                    ? 'bg-purple-500'
                    : 'bg-chariot-text-disabled'
                )}
              />
              {flagsData.includes(FlagType.STATIC) ? 'Static' : 'Dynamic'}
            </span>
          </Loader>
        </div>

        {flagsData.includes(FlagType.STATIC) ? (
          <div className="flex items-start gap-2">
            <h4 className="text-chariot-text-secondary min-w-[60px] font-bold">
              Address:
            </h4>
            <Loader
              className="text-chariot-text-primary font-mono"
              isLoading={isLoadingFlags}
            >
              {STATIC_IP_ADDRESS}
            </Loader>
          </div>
        ) : (
          <div className="flex items-start gap-2">
            <span className="text-chariot-text-tertiary text-xs">
              Addresses rotate automatically
            </span>
          </div>
        )}
      </SettingEditableCard>

      {/* Scan Schedule Card */}
      <SettingEditableCard
        data-testid="scan-schedule-card"
        editConfig={{
          modalTitle: 'Scan Schedule',
          formComponent: ScanSchedule,
          formProps: { customerEmail: email },
          modalSize: '5xl',
          modalClassName: 'px-8 py-6',
          hideFooter: true,
        }}
        editable={editable}
      >
        <div className="flex items-center gap-2">
          <h4 className="font-bold">Schedule</h4>
        </div>
        <div className="flex items-center gap-2">
          <h4 className="font-bold">Days:</h4>
          <Loader
            className="text-chariot-text-secondary w-12"
            isLoading={isLoadingScanSchedule}
          >
            {currentScanSchedule?.daysName &&
            currentScanSchedule.daysName.length > 0
              ? currentScanSchedule.daysName.filter(Boolean).join(', ')
              : 'All days'}
          </Loader>
        </div>
        <div className="flex items-center gap-2">
          <h4 className="font-bold">Time Window:</h4>
          <Loader
            className="text-chariot-text-secondary w-12"
            isLoading={isLoadingScanSchedule}
          >
            {currentScanSchedule?.start && currentScanSchedule?.end
              ? `${currentScanSchedule.start} - ${currentScanSchedule.end}`
              : 'All hours'}
          </Loader>
        </div>
        {currentScanSchedule?.manualBypass && (
          <div className="flex items-center gap-2">
            <h4 className="font-bold">Manual Bypass:</h4>
            <span className="text-chariot-text-secondary">Enabled</span>
          </div>
        )}
      </SettingEditableCard>

      {/* Rate Limiting Card */}
      <SettingEditableCard
        data-testid="rate-limiting-card"
        editConfig={{
          modalTitle: 'Rate Limiting',
          formComponent: RateLimitingForm,
          formProps: { customerEmail: email },
          modalSize: '4xl',
          modalClassName: 'px-8 py-6',
        }}
        editable={editable}
      >
        <div className="flex items-center gap-2">
          <h4 className="font-bold">Rate Limiting</h4>
        </div>
        <div className="flex items-center gap-2">
          <h4 className="font-bold">Simultaneous hosts scanned:</h4>
          <Loader
            className="text-chariot-text-secondary w-12"
            isLoading={rateLimitingStatus === 'pending'}
          >
            {rateLimitSetting?.simultaneousHosts !== undefined
              ? rateLimitSetting.simultaneousHosts.toString()
              : RATE_LIMIT_SYSTEM_DEFAULTS.SIMULTANEOUS_HOSTS.toString()}
          </Loader>
        </div>
        <div className="flex items-center gap-2">
          <h4 className="font-bold">Max requests per host:</h4>
          <Loader
            className="text-chariot-text-secondary w-12"
            isLoading={rateLimitingStatus === 'pending'}
          >
            {rateLimitSetting?.capabilityRateLimit !== undefined
              ? `${rateLimitSetting.capabilityRateLimit} req/s`
              : `${RATE_LIMIT_SYSTEM_DEFAULTS.REQUESTS_PER_HOST} req/s`}
          </Loader>
        </div>
      </SettingEditableCard>

      {/* Scan Profiles Card */}
      <SettingEditableCard
        data-testid="scan-profiles-card"
        editConfig={{
          modalTitle: 'Scan Profiles',
          formComponent: ScanProfilesForm,
          formProps: { customerEmail: email },
          modalSize: '4xl',
          modalClassName: 'px-8 py-6',
        }}
        editable={editable}
      >
        <div className="flex items-center gap-2">
          <h4 className="font-bold">Scan Profiles</h4>
        </div>
        {scanProfiles.length === 0 ? (
          <p className="text-chariot-text-secondary text-sm">
            No profiles configured. Create profiles to set per-asset rate
            limits, then assign them via{' '}
            <span className="font-medium">Manage → Change Scan Profile</span> in
            any asset or seed table.
          </p>
        ) : (
          <>
            {scanProfiles.map(profile => (
              <div key={profile.id} className="flex items-center gap-2">
                <h4 className="font-bold">{profile.name}:</h4>
                <Loader
                  className="text-chariot-text-secondary w-12"
                  isLoading={rateLimitingStatus === 'pending'}
                >
                  {profile.simultaneousHosts
                    ? `${profile.simultaneousHosts} hosts`
                    : 'Global'}{' '}
                  /{' '}
                  {profile.capabilityRateLimit
                    ? `${profile.capabilityRateLimit} req/s`
                    : 'Global'}
                </Loader>
              </div>
            ))}
          </>
        )}
      </SettingEditableCard>
    </div>
  );
};
